import type { RangeSliderValue } from '../../experimental/atoms/range-slider';
import type { RangeFilter } from './filter-types';

const parseBound = (raw: unknown): number | undefined => {
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? raw : undefined;
  }
  if (typeof raw !== 'string' || raw.trim() === '') {
    return undefined;
  }
  const bound = Number(raw);
  return Number.isFinite(bound) ? bound : undefined;
};

/**
 * The range a filter's stored bounds describe, or undefined when neither is
 * set. A missing bound falls back to the slider's end, so a link carrying
 * only a lower bound still shows it.
 */
export const readRangeFilterValue = (
  filterValues: Record<string, unknown>,
  filter: Pick<RangeFilter, 'minKey' | 'maxKey' | 'min' | 'max'>,
): RangeSliderValue | undefined => {
  const lower = parseBound(filterValues[filter.minKey]);
  const upper = parseBound(filterValues[filter.maxKey]);
  if (lower === undefined && upper === undefined) {
    return undefined;
  }
  const clamp = (bound: number) =>
    Math.min(filter.max, Math.max(filter.min, bound));
  return [clamp(lower ?? filter.min), clamp(upper ?? filter.max)];
};

/**
 * The bounds to store for a committed range, as `[lower, upper]`. Both are
 * stored exactly as set, ends of the scale included: once the filter is in
 * use, its bounds are real bounds.
 */
export const toRangeFilterBounds = ([lower, upper]: RangeSliderValue): [
  string,
  string,
] => [String(lower), String(upper)];
